import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { QueryKey } from '../utils/api';
import { isServer } from '../utils/utils';
import type { StorageKey } from './model/StorageKey';

export class LocalStorage {
    set(key: StorageKey, value: string): void {
        window.localStorage.setItem(key, value);
    }

    get(key: StorageKey): string | null {
        return window.localStorage.getItem(key);
    }

    delete(key: StorageKey): void {
        window.localStorage.removeItem(key);
    }
}

export const useLocalStorage = <T>(key: StorageKey): UseQueryResult<T | undefined> =>
    useQuery({
        queryFn: (): T | null => {
            const storage = new LocalStorage();
            const value = storage.get(key);
            try {
                return value ? (JSON.parse(value) as T) : null;
            } catch (e) {
                return value as T;
            }
        },
        keepPreviousData: true,
        queryKey: [QueryKey.LOCAL_STORAGE, key],
        enabled: !isServer,
        refetchOnWindowFocus: 'always',
    });

export const useLocalStorageMutation = <T>(
    key: StorageKey,
    keysToInvalidate?: string[][],
): UseMutationResult<unknown, unknown, T> => {
    const queryClient = useQueryClient();
    const storage = new LocalStorage();

    return useMutation<unknown, unknown, T>({
        mutationFn: async (value: T | undefined | null) => {
            if (value) {
                storage.set(key, typeof value === 'string' ? value : JSON.stringify(value));
            } else {
                storage.delete(key);
            }

            return Promise.resolve();
        },
        onSuccess: async () => {
            const promises = (keysToInvalidate || [])
                .map((keyToInvalidate) =>
                    queryClient.invalidateQueries(keyToInvalidate, { exact: false }),
                )
                .concat(queryClient.invalidateQueries([QueryKey.LOCAL_STORAGE, key]));

            await Promise.all(promises);
        },
    });
};
